import React, { useEffect, useState, lazy } from 'react';
import { HeartIcon, ShieldIcon, SparklesIcon, LeafIcon, BrainIcon, ChevronLeftIcon, ChevronRightIcon, StarIcon } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
export function GutBenefitsSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const headerReveal = useScrollReveal({
    threshold: 0.2
  });
  const imageReveal = useScrollReveal({
    threshold: 0.2
  });
  const benefitsReveal = useScrollReveal({
    threshold: 0.1
  });
  const benefits = [{
    icon: <HeartIcon className="w-6 h-6" data-id="element-170" />,
    title: '40% Punicalagins Potency',
    description: "Most pomegranate supplements use weak extracts with minimal active compounds. Ours delivers 3,750mg equivalent with 40% punicalagins — the compound responsible for pomegranate's heart and energy benefits.*"
  }, {
    icon: <BrainIcon className="w-6 h-6" data-id="element-171" />,
    title: 'Three Compounds. One Circuit.',
    description: 'Punicalagins, ellagitannins & urolithin A work together to support the heart-energy-restore system that breaks down when your body feels depleted and worn down.'
  }, {
    icon: <LeafIcon className="w-6 h-6" data-id="element-172" />,
    title: 'Clean Energy. No Crashes.',
    description: 'Unlike caffeine or sugar-loaded energy drinks, this is steady, sustained vitality — no jitters, no afternoon crash, no conflict with your faith.'
  }, {
    icon: <SparklesIcon className="w-6 h-6" data-id="element-173" />,
    title: 'Cellular Renewal at the Source',
    description: "Pomegranate's ellagitannins convert to Urolithin A — clinically shown to support mitochondrial health, your cells' energy generators. Real energy. Not borrowed from tomorrow.*"
  }];
  // Auto-advance slideshow
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % benefits.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, benefits.length]);
  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
  };
  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % benefits.length);
    setIsAutoPlaying(false);
  };
  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + benefits.length) % benefits.length);
    setIsAutoPlaying(false);
  };
  return <section className="w-full bg-[#f5f1ed] py-16 lg:py-24" data-id="element-174">
      <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-175">
        {/* Section Header */}
        <div ref={headerReveal.ref} className={`text-center mb-12 transition-all duration-1000 ${headerReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-176">
          <h2 className="font-serif text-3xl lg:text-4xl font-bold text-[#2d4a3a] mb-4" data-id="element-177">
            God Already Created the Answer.
          </h2>
          <p className="text-gray-600 text-xs max-w-2xl mx-auto mb-6" data-id="element-178">
            The biblical botanical with 3 active compounds clinically shown to
            support heart health, daily vitality & natural energy — starting
            within your first week.
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-center" data-id="element-179">
          {/* Left: Image */}
          <div ref={imageReveal.ref} className={`order-1 lg:order-1 transition-all duration-1000 ${imageReveal.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`} data-id="element-180">
            <div className="relative" data-id="element-181">
              <img src="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/imgi_24_Frame17393323731_3356911832620697_-_Copy.png?v=1763935221" alt="Faith & Fig Divine Wellness Product" className="w-full max-w-md mx-auto rounded-2xl shadow-xl transform hover:scale-105 transition-transform duration-300" data-id="element-182" />
              {/* Floating Badge */}
              <div className="absolute -top-4 -right-4 bg-[#496f58] text-white rounded-full w-24 h-24 flex flex-col items-center justify-center shadow-xl animate-bounce" data-id="element-183">
                <span className="text-2xl font-bold" data-id="element-184">90%</span>
                <span className="text-[8px] text-center leading-tight" data-id="element-185">
                  Feel Better
                </span>
              </div>
            </div>
          </div>

          {/* Right: Slideshow */}
          <div ref={benefitsReveal.ref} className="order-2 lg:order-2" data-id="element-186">
            {/* Slideshow Container */}
            <div className="relative min-h-[140px] mb-4" data-id="element-187">
              {benefits.map((benefit, index) => <div key={index} className={`absolute inset-0 transition-all duration-700 ${index === currentSlide ? 'opacity-100 translate-x-0 pointer-events-auto' : 'opacity-0 translate-x-12 pointer-events-none'}`} data-id="element-188">
                  <div className="flex items-start gap-4 group" data-id="element-189">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#496f58] text-white flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-190">
                      {benefit.icon}
                    </div>
                    <div data-id="element-191">
                      <h3 className="font-bold text-lg text-[#2d4a3a] mb-2" data-id="element-192">
                        {benefit.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed" data-id="element-193">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>

            {/* Navigation Controls */}
            <div className="flex items-center justify-center gap-1 mb-3" data-id="element-194">
              {/* Previous Button */}

              {/* Dot Indicators */}

              {/* Next Button */}
            </div>
          </div>
        </div>
      </div>
    </section>;
}